import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LionsStripe from '../components/LionsStripe';
import events from '../data/events';
import { socialAccounts } from '../components/SocialIcons';
import { 
  UserGroupIcon, 
  CalendarDaysIcon,
  BookOpenIcon,
  DocumentTextIcon,
  MapPinIcon,
  PhoneIcon,
  ShieldCheckIcon,
  HeartIcon,
  AcademicCapIcon,
  TrophyIcon,
  ArrowRightIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

// The next three dates from the shared school calendar, so the homepage never
// drifts out of sync with /calendar.
const getUpcomingEvents = (count = 3) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return events
    .filter((event) => (event.endDate || event.date) >= today)
    .sort((a, b) => a.date - b.date)
    .slice(0, count)
    .map((event) => ({
      id: event.id,
      title: event.title,
      month: event.date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      day: String(event.date.getDate()).padStart(2, '0'),
      description: event.description,
      time: event.allDay ? 'All Day' : event.time || 'See calendar'
    }));
};

// Full literal class names so Tailwind keeps them
const NAVY_TILE = 'bg-primary hover:bg-navy-600';
const RUST_TILE = 'bg-orange-700 hover:bg-orange-800';

const QUICK_LINKS = [
  { icon: BookOpenIcon, title: 'Handbook', href: '/handbook', tile: NAVY_TILE },
  { icon: UserGroupIcon, title: 'Parent Portal', href: '/parent-portal', tile: RUST_TILE },
  { icon: DocumentTextIcon, title: 'Policies', href: '/cell-phone-policy', tile: NAVY_TILE },
  { icon: CalendarDaysIcon, title: 'Calendar', href: '/calendar', tile: RUST_TILE },
  { icon: ShieldCheckIcon, title: 'Uniforms', href: '/uniform-policy', tile: NAVY_TILE },
  { icon: HeartIcon, title: 'Wellness', href: '/mental-health-resources', tile: RUST_TILE },
  { icon: UserGroupIcon, title: 'Directory', href: '/staff-directory', tile: NAVY_TILE },
  { icon: MapPinIcon, title: 'Contact', href: '/contact', tile: RUST_TILE }
];

// Stat blocks in school colours (text colours chosen for >= 4.5:1)
const STAT_TILES = [
  'bg-accent-yellow text-primary',
  'bg-white text-primary',
  'bg-secondary text-primary',
  'bg-navy-400 text-white'
];

const HomePage = () => {
  const [scrollY, setScrollY] = useState(0);
  const upcomingEvents = getUpcomingEvents();

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // School data for The Lexington Academy
  const schoolData = {
    name: 'The Lexington Academy',
    tagline: 'Home of the Lions',
    address: '131 East 104 St, Manhattan, NY 10029',
    phone: '212-860-5831',
    fax: '212-860-6094',
    mission: 'The Lexington Academy seeks to maintain an educational environment in which the entire school community, in partnership with community based organizations and families, works to ensure that all students are provided with the skills to become lifelong learners.',
    
    news: [
      {
        id: 1,
        title: 'NYCPS Monthly Updates',
        category: 'Family Update',
        excerpt: 'Important dates, admissions deadlines, parent-teacher conferences, and more from NYC Public Schools. Read the latest update.',
        date: 'Nov 2026',
        link: '/news/monthly-updates',
        image: '/assets/images/news/nycps-monthly-updates-high-school-fair.jpg'
      },
      {
        id: 2,
        title: 'Device Free Learning',
        category: 'Policy Update',
        excerpt: 'New distraction-free learning policies are in effect for the 2026-2027 academic year to enhance student focus.',
        date: 'Aug 28',
        link: '/cell-phone-policy',
        image: '/assets/images/news/device-free-learning-students-reading.jpg'
      },
      {
        id: 3,
        title: 'Summer Meals Program',
        category: 'Community',
        excerpt: 'Free nutritious meals are available for all students throughout the summer at designated locations.',
        date: 'Jun 15',
        link: '/news/free-summer-meals',
        image: '/assets/images/news/summer-meals-kids-eating.jpg'
      },
    ],

    events: upcomingEvents,

    stats: [
      { label: 'Students Enrolled', value: '450+', icon: UserGroupIcon },
      { label: 'Dedicated Faculty', value: '35+', icon: AcademicCapIcon },
      { label: 'Attendance Rate', value: '95%', icon: SparklesIcon },
      { label: 'Years of Excellence', value: '20+', icon: TrophyIcon },
    ],
  };

  return (
    <main id="main-content" className="bg-primary selection:bg-secondary selection:text-white font-sans">
      <Navbar />

      {/* ============================================================================
          HERO SECTION - Immersive & Sleek
      ============================================================================ */}
      <section className="relative flex h-[100svh] min-h-[560px] w-full flex-col overflow-hidden bg-primary">
        {/* Video Background with Parallax Effect */}
        <div 
          className="absolute inset-0 transition-transform duration-100 ease-out"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
            tabIndex={-1}
            className="w-full h-full object-cover scale-105"
            poster="https://i0.wp.com/psms72lexingtonacademy.net/wp-content/uploads/2023/12/DJI_0591-scaled.jpg?fit=2560%2C1440&ssl=1"
          >
            <source src="/assets/videos/home/Lexington Academy.mp4" type="video/mp4" />
            <img 
              src="https://i0.wp.com/psms72lexingtonacademy.net/wp-content/uploads/2023/12/DJI_0591-scaled.jpg?fit=2560%2C1440&ssl=1"
              alt="The Lexington Academy Building" 
              className="w-full h-full object-cover"
            />
          </video>
          {/* Gentle top/bottom vignette only: no text sits on the footage any
              more, so it no longer needs a heavy scrim. */}
          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-primary/40 via-transparent to-primary/40"></div>
        </div>

        {/* Lower-third band: the hero copy sits on a slim, SOLID navy strip
            pinned to the bottom of the hero, so the footage stays unobstructed
            above it. The backing is opaque on purpose: axe cannot resolve a
            contrast ratio for text over a <video>, so every line here needs a
            solid surface to stay measurable. */}
        <div className="relative z-10 mt-auto w-full border-t-2 border-secondary bg-primary">
          <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-6 py-3.5 md:px-10 md:py-4 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
            <div className="min-w-0">
              {/* Eyebrow and intro stay in the accessibility tree; visually
                  the band carries only the title and one tagline line. */}
              <p className="sr-only">Welcome to The Lexington Academy</p>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-extrabold leading-tight tracking-tight text-white">
                <span className="whitespace-nowrap">PS/MS 72M</span>
              </h1>
              <p className="mt-0.5 text-xs md:text-sm font-semibold text-orange-400">
                The Lexington Academy &middot; Home of the Lions
              </p>
              <p className="sr-only">
                Cultivating intellect, character, and community in the heart of East Harlem. A place where every student rises to their full potential.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-3 lg:shrink-0">
              <a href="#about" className="group inline-flex items-center justify-center gap-2 rounded-full bg-orange-700 hover:bg-orange-800 px-3 py-2.5 text-xs font-bold uppercase text-center tracking-[0.06em] sm:px-4 sm:tracking-[0.14em] text-white transition-colors duration-300">
                <span>Discover Our School</span>
                <span className="hidden sm:inline-flex">
                  <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 motion-reduce:transform-none transition-transform" aria-hidden="true" />
                </span>
              </a>
              <a href="/contact" className="inline-flex items-center justify-center rounded-full border-2 border-white/30 hover:border-white hover:bg-white/10 px-3 py-2 text-xs font-bold uppercase text-center tracking-[0.06em] sm:px-4 sm:tracking-[0.14em] text-white transition-colors duration-300">
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================================
          PRINCIPAL'S WELCOME - light-blue band, portrait in a gold ring
      ============================================================================ */}
      <section className="bg-accent-light">
        <LionsStripe />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <a
            href="/principals-message"
            className="group grid md:grid-cols-[auto_1fr] gap-10 md:gap-14 items-center rounded-3xl focus:outline-none focus-visible:ring-4 focus-visible:ring-primary focus-visible:ring-offset-4 focus-visible:ring-offset-accent-light"
          >
            <div className="relative mx-auto md:mx-0">
              <div aria-hidden="true" className="absolute -inset-3 rounded-full bg-accent-yellow"></div>
              <div aria-hidden="true" className="absolute -inset-3 rounded-full border-4 border-dashed border-primary/30"></div>
              <img
                src="/assets/images/Dr. Tracy Smith, Principal.jpg"
                alt=""
                aria-hidden="true"
                className="relative w-44 h-44 md:w-60 md:h-60 rounded-full object-cover border-8 border-primary shadow-2xl group-hover:scale-[1.03] motion-reduce:transform-none transition-transform duration-500"
              />
            </div>

            <div>
              <span className="text-orange-700 font-extrabold tracking-[0.2em] text-sm uppercase mb-3 block">
                A Message from Our Principal
              </span>
              <h2 className="text-3xl md:text-5xl font-extrabold text-primary tracking-tight leading-tight">
                Welcome to The Lexington Academy
              </h2>
              <p className="mt-5 text-xl md:text-2xl text-primary leading-relaxed max-w-3xl">
                &ldquo;We remain steadfast in our commitment to educating the whole child &mdash;
                creating an environment where every student is valued, challenged, and inspired to
                reach their fullest potential.&rdquo;
              </p>
              <p className="mt-5 text-base">
                <span className="font-extrabold text-primary">Dr. Tracy Smith</span>
                <span className="text-gray-700"> &middot; Principal</span>
              </p>
              <span className="mt-7 inline-flex items-center gap-2 bg-orange-700 group-hover:bg-orange-800 text-white px-7 py-3.5 rounded-full font-bold shadow-lg transition-colors duration-300">
                Read the Full Message
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 motion-reduce:transform-none transition-transform" aria-hidden="true" />
              </span>
            </div>
          </a>
        </div>
      </section>

      {/* ============================================================================
          QUICK LINKS - gold band, navy and rust tiles
      ============================================================================ */}
      <section aria-labelledby="quick-links-heading" className="bg-accent-yellow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
          <h2 id="quick-links-heading" className="text-primary font-extrabold tracking-[0.2em] text-sm uppercase mb-6">
            Quick Links
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4">
            {QUICK_LINKS.map((item) => (
              <a
                key={item.title}
                href={item.href}
                className={`group flex items-center gap-3 rounded-2xl p-3 text-white sm:flex-col sm:justify-center sm:text-center sm:aspect-square sm:p-4 shadow-lg hover:shadow-2xl hover:-translate-y-1 motion-reduce:transform-none transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-accent-yellow ${item.tile}`}
              >
                <span className="flex-shrink-0 p-2.5 sm:p-3 rounded-xl bg-white/10 text-accent-yellow group-hover:bg-accent-yellow group-hover:text-primary transition-colors duration-300">
                  <item.icon className="w-7 h-7" aria-hidden="true" />
                </span>
                <span className="text-sm font-bold leading-tight">{item.title}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================================
          ABOUT - navy band, bold stat blocks in school colours
      ============================================================================ */}
      <section id="about" className="relative bg-primary text-white overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            <div className="space-y-8">
              <div>
                <span className="text-accent-yellow font-extrabold tracking-[0.2em] text-sm uppercase mb-3 block">About Us</span>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                  Building Future <span className="text-secondary">Leaders</span>
                </h2>
              </div>

              <div className="space-y-5 text-lg text-white/90 leading-relaxed">
                <p>
                  The Lexington Academy is more than just a school; it's a vibrant community hub in East Harlem. We believe in the power of partnership between educators, families, and community organizations to create a nurturing environment.
                </p>
                <p>
                  Our rigorous, standards-driven curriculum is designed to challenge students while providing the support they need to thrive. We don't just teach subjects; we foster character, creativity, and critical thinking.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-6 rounded-2xl bg-navy-600 border-l-8 border-accent-yellow">
                  <h3 className="font-extrabold text-accent-yellow text-xl mb-2">Mission</h3>
                  <p className="text-white/90 leading-relaxed">Empowering students with skills to become lifelong learners through community partnership.</p>
                </div>
                <div className="p-6 rounded-2xl bg-navy-600 border-l-8 border-secondary">
                  <h3 className="font-extrabold text-secondary text-xl mb-2">Vision</h3>
                  <p className="text-white/90 leading-relaxed">Creating a safe, inclusive environment where academic excellence meets character development.</p>
                </div>
              </div>

              <a
                href="/mission-statement"
                className="group inline-flex items-center gap-2 text-accent-yellow font-bold text-lg border-b-2 border-accent-yellow/40 hover:border-accent-yellow pb-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow focus-visible:ring-offset-4 focus-visible:ring-offset-primary rounded-sm"
              >
                Read our full mission
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 motion-reduce:transform-none transition-transform" aria-hidden="true" />
              </a>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {schoolData.stats.map((stat, index) => (
                <div
                  key={stat.label}
                  className={`p-7 md:p-8 rounded-3xl shadow-2xl flex flex-col justify-between min-h-[170px] ${STAT_TILES[index]} ${index === 0 ? 'col-span-2' : ''}`}
                >
                  <stat.icon className="w-10 h-10 mb-4 opacity-90" aria-hidden="true" />
                  <div>
                    <div className="text-5xl md:text-6xl font-extrabold tracking-tight mb-1">{stat.value}</div>
                    <div className="text-sm font-bold uppercase tracking-wider">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================================
          FOLLOW OUR STORY - social band (hidden until accounts are set in
          src/data/social.js)
      ============================================================================ */}
      {socialAccounts.length > 0 && (() => {
        const [primary, secondary] = socialAccounts;
        const PrimaryIcon = primary.Icon;
        return (
          <section aria-labelledby="follow-heading" className="bg-secondary">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 gap-10 items-center">
              <div>
                <span className="text-primary font-bold tracking-widest text-sm uppercase mb-2 block">Stay Connected</span>
                <h2 id="follow-heading" className="text-4xl md:text-5xl font-bold text-primary tracking-tight mb-4">
                  Follow Our Story
                </h2>
                <p className="text-lg text-primary max-w-md leading-relaxed">
                  See daily moments from our classrooms, hallways, and community.
                </p>
              </div>

              <div className="flex flex-col items-start md:items-end gap-4">
                <a
                  href={primary.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-primary text-white px-8 py-5 rounded-2xl text-lg font-bold shadow-xl hover:bg-navy-600 hover:-translate-y-0.5 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
                >
                  <PrimaryIcon className="w-6 h-6" />
                  {primary.handle ? `${primary.handle} on ${primary.label}` : `Follow us on ${primary.label}`}
                  <span className="sr-only">(opens in a new tab)</span>
                </a>
                {secondary && (
                  <a
                    href={secondary.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary font-semibold underline underline-offset-4 hover:text-navy-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-secondary rounded"
                  >
                    Also on {secondary.label}{secondary.handle ? ` \u2192 ${secondary.handle}` : ''}
                    <span className="sr-only">(opens in a new tab)</span>
                  </a>
                )}
              </div>
            </div>
          </section>
        );
      })()}

      {/* ============================================================================
          NEWS & UPDATES - light-blue band, white cards
      ============================================================================ */}
      <section className="bg-accent-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-12 gap-6">
            <div>
              <span className="text-orange-700 font-extrabold tracking-[0.2em] text-sm uppercase mb-3 block">Happening Now</span>
              <h2 className="text-4xl md:text-5xl font-extrabold text-primary tracking-tight">Latest News &amp; Updates</h2>
            </div>
            <a
              href="/news"
              className="group inline-flex items-center gap-2 self-start md:self-auto bg-primary text-white px-6 py-3 rounded-full font-bold hover:bg-navy-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-accent-light"
            >
              View all news
              <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 motion-reduce:transform-none transition-transform" aria-hidden="true" />
            </a>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {schoolData.news.map((item) => (
              <article
                key={item.id}
                className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-1 motion-reduce:transform-none transition-all duration-300 flex flex-col h-full border-b-8 border-secondary focus-within:ring-4 focus-within:ring-primary focus-within:ring-offset-4 focus-within:ring-offset-accent-light"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={item.image}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 motion-reduce:transform-none transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4 bg-primary px-3 py-1.5 rounded-full text-xs font-extrabold text-white uppercase tracking-wider shadow">
                    {item.category}
                  </div>
                </div>
                <div className="p-7 flex-1 flex flex-col">
                  <div className="text-sm text-orange-700 font-bold mb-2">{item.date}</div>
                  <h3 className="text-2xl font-extrabold text-primary mb-3 leading-tight">
                    {/* The whole card is clickable via a pseudo-element, so no
                        real element sits on top of the card text */}
                    <a href={item.link} className="after:absolute after:inset-0 after:content-[''] focus:outline-none">
                      {item.title}
                    </a>
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-6 flex-1">{item.excerpt}</p>
                  <div className="flex items-center text-orange-700 font-bold">
                    Read Story <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 motion-reduce:transform-none transition-transform" aria-hidden="true" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================================
          EVENTS & COMMUNITY - deep navy band, gold date blocks, rust panel
      ============================================================================ */}
      <section className="relative bg-navy-700 text-white overflow-hidden">
        <LionsStripe />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4 mb-10">
                <div className="p-3 rounded-2xl bg-accent-yellow text-primary">
                  <CalendarDaysIcon className="w-8 h-8" aria-hidden="true" />
                </div>
                <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight">Upcoming Events</h2>
              </div>

              <div className="space-y-5">
                {schoolData.events.map((event) => (
                  <div key={event.id} className="flex gap-5 p-5 rounded-2xl bg-white text-primary shadow-xl">
                    <div className="flex-shrink-0 w-20 h-20 rounded-xl bg-accent-yellow flex flex-col items-center justify-center text-center">
                      <span className="text-xs font-extrabold uppercase tracking-wider">{event.month}</span>
                      <span className="text-3xl font-extrabold tracking-tighter leading-none">{event.day}</span>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-xl font-extrabold mb-1 leading-snug">{event.title}</h3>
                      <p className="text-gray-700 text-sm mb-2">{event.description}</p>
                      <span className="inline-flex items-center text-xs font-bold bg-primary text-white px-2.5 py-1 rounded-full">
                        {event.time}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <a
                href="/calendar"
                className="mt-8 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-accent-yellow text-primary font-extrabold hover:bg-white transition-colors w-full sm:w-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow focus-visible:ring-offset-4 focus-visible:ring-offset-navy-700"
              >
                View Full Calendar
                <ArrowRightIcon className="w-5 h-5" aria-hidden="true" />
              </a>
            </div>

            <div className="lg:col-span-5">
              <div className="relative h-full bg-orange-700 rounded-3xl p-10 shadow-2xl overflow-hidden flex flex-col justify-between">
                <div aria-hidden="true" className="hidden sm:block absolute -top-16 -right-16 w-56 h-56 rounded-full border-[32px] border-white/10 pointer-events-none"></div>

                <div className="relative space-y-8">
                  <div>
                    <h3 className="text-3xl font-extrabold mb-2">Join Our Community</h3>
                    <p className="text-white/95 text-lg">
                      Experience the difference of a supportive, academic-focused environment.
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="p-2.5 bg-white/15 rounded-xl">
                        <MapPinIcon className="w-6 h-6 text-white" aria-hidden="true" />
                      </div>
                      <div>
                        <h4 className="font-extrabold">Visit Us</h4>
                        <p className="text-white/95">{schoolData.address}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-2.5 bg-white/15 rounded-xl">
                        <PhoneIcon className="w-6 h-6 text-white" aria-hidden="true" />
                      </div>
                      <div>
                        <h4 className="font-extrabold">Call Us</h4>
                        <p className="text-white/95">{schoolData.phone}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <a
                  href="/contact"
                  className="relative mt-10 block w-full bg-white text-primary text-center py-4 rounded-2xl font-extrabold hover:bg-accent-yellow transition-colors shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-orange-700"
                >
                  Get in Touch
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default HomePage;
